import { useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { ROUTES } from '~/utils/routes';
import { type CreateUserFormValues, generatePostRequestBody, userFormSchema } from '../logics';
import { type CreateUserFormPresentation } from '../presentations';
import { useGetClientBranches } from './useGetClientBranches';
import { usePostClientUser } from './usePostClientUser';

type IUseCreateUserForm = () => {
  state: {
    methods: ReturnType<typeof useForm<CreateUserFormValues>>;
    branchOptions: React.ComponentProps<typeof CreateUserFormPresentation>['branchOptions'];
    isMutating: boolean;
  };
  actions: {
    handleSubmit: React.ComponentProps<'form'>['onSubmit'];
    handleCancel: () => void;
  };
};

/**
 * 利用者新規作成フォームの状態管理とアクションを提供する
 */
export const useCreateUserForm: IUseCreateUserForm = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const threeLetterCode = getThreeLetterCode() || '';

  // 事業所一覧の取得
  const { data: branchesData } = useGetClientBranches({ threeLetterCode });
  const branchOptions = (branchesData.branches ?? []).map(branch => ({
    value: String(branch.id),
    label: `${branch.name}（${branch.code}）`,
  }));

  // react-hook-form の初期化
  const methods = useForm<CreateUserFormValues>({
    defaultValues: {
      fullName: '',
      email: '',
      branchId: '',
      billableFrom: null,
    },
    criteriaMode: 'all',
    mode: 'onBlur',
    resolver: zodResolver(userFormSchema),
  });

  // 利用者新規作成 API hook
  const { trigger, isMutating } = usePostClientUser(threeLetterCode, {
    onSuccess: () => {
      showToast({ message: '利用者を作成しました。', status: 'success' });
      navigate(ROUTES.USERS);
    },
    onError: () => {
      showToast({ message: '利用者の作成に失敗しました。', status: 'danger' });
    },
  });

  /**
   * フォームを送信する
   */
  const handleSubmit = methods.handleSubmit(formValues => {
    trigger(generatePostRequestBody({ formValues }));
  });

  /**
   * 利用者一覧画面へ戻る
   */
  const handleCancel = () => {
    navigate(ROUTES.USERS);
  };

  return {
    state: { methods, branchOptions, isMutating },
    actions: { handleSubmit, handleCancel },
  };
};
